import { useState } from 'react';
import PropertyInputWrapper from './PropertyInputWrapper';
import PropertyDisplayWrapper from './PropertyDisplayWrapper';
import styled from 'styled-components';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { useCardContextValue } from '../state/CardContext';
import { usePropertyDisplay } from '../hooks/usePropertyDisplay';
import I18n from 'I18n';

/**
 * Wrapper for FormattedPropertyV2 to make them align with the label and appear clickable.
 * Uses on-hover styling similar to floating property inputs.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const DisplayWrapper = styled.div.withConfig({
  displayName: "ClickablePropertyInputWrapper__DisplayWrapper",
  componentId: "a0yapn-0"
})(["position:relative;white-space:nowrap;line-height:var(--trellis-typography-body-100-line-height);&::after{content:'';position:absolute;bottom:0;left:0;width:100%;height:1px;background-color:transparent;}&:hover{&::after{background-color:", ";}}"], ({
  theme
}) => theme.color['border-core-default']);

/**
 * Wrapper for non-clickable property display to align with the label.
 */
const NonClickableDisplayWrapper = styled.div.withConfig({
  displayName: "ClickablePropertyInputWrapper__NonClickableDisplayWrapper",
  componentId: "a0yapn-1"
})(["line-height:var(--trellis-typography-body-100-line-height);border-bottom:", ";"], ({
  theme
}) => `${theme.borderWidth['100']} solid transparent`);
const ClickablePropertyInputWrapper = ({
  propertyName,
  isEditable = true
}) => {
  var _subjectPermissionsRe, _subjectPermissionsRe2, _propertyDefinitionsR;
  const [isActivated, setIsActivated] = useState(false);
  const {
    objectTypeId,
    asyncData: {
      propertyDefinitionsResult,
      subjectPermissionsResult
    }
  } = useCardContextValue();
  const {
    getPropertyDisplay
  } = usePropertyDisplay(objectTypeId, (_subjectPermissionsRe = (_subjectPermissionsRe2 = subjectPermissionsResult.data) === null || _subjectPermissionsRe2 === void 0 || (_subjectPermissionsRe2 = _subjectPermissionsRe2.crmObject) === null || _subjectPermissionsRe2 === void 0 ? void 0 : _subjectPermissionsRe2.userPermissions.currentUserCanEdit) !== null && _subjectPermissionsRe !== void 0 ? _subjectPermissionsRe : false);
  const propertyDefinition = (_propertyDefinitionsR = propertyDefinitionsResult.data) === null || _propertyDefinitionsR === void 0 ? void 0 : _propertyDefinitionsR.get(propertyName);
  if (!propertyDefinition) {
    return null;
  }
  const {
    isReadOnly
  } = getPropertyDisplay(propertyDefinition.property);
  const canEdit = isEditable && !isReadOnly;
  const isEditing = isActivated && canEdit;
  if (isEditing) {
    return /*#__PURE__*/_jsx(PropertyInputWrapper, {
      propertyName: propertyName,
      isEditable: isEditable,
      autofocus: true
    });
  }
  const content = /*#__PURE__*/_jsx(NonClickableDisplayWrapper, {
    children: /*#__PURE__*/_jsx(TruncateString, {
      useFlex: true,
      children: /*#__PURE__*/_jsx(PropertyDisplayWrapper, {
        propertyName: propertyName
      })
    })
  });
  if (!canEdit) {
    return content;
  }
  return /*#__PURE__*/_jsx(DisplayWrapper, {
    onClick: () => setIsActivated(true),
    tabIndex: 0,
    role: "button",
    "aria-label": I18n.text('ObjectHighlightCard.clickablePropertyAriaLabel', {
      propertyName: propertyDefinition.property.label
    }),
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        setIsActivated(true);
      }
    },
    children: content
  });
};
export default ClickablePropertyInputWrapper;